import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Plus, Trash, FloppyDisk, Lock, MagnifyingGlass, Check, X, Shield, ArrowsClockwise, Users } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RESOURCE_LABELS_ES = {
  dashboard: 'Dashboard', agenda: 'Agenda', volunteers: 'Voluntarios',
  studies: 'Estudios', clients: 'Clientes', warehouse: 'Almacen',
  settings: 'Configuracion', audit: 'Auditoria', communications: 'Comunicaciones',
  recruitment: 'Reclutamiento', portal_admin: 'Portal Cliente',
  queries: 'Queries', adverse_events: 'Eventos Adversos',
  support: 'Soporte', payments: 'Pagos a voluntarios', qsalud: 'Q Salud',
  statistics: 'Estadisticas',
};
const RESOURCE_LABELS_EN = {
  dashboard: 'Dashboard', agenda: 'Agenda', volunteers: 'Volunteers',
  studies: 'Studies', clients: 'Clients', warehouse: 'Warehouse',
  settings: 'Settings', audit: 'Audit', communications: 'Communications',
  recruitment: 'Recruitment', portal_admin: 'Client Portal',
  queries: 'Queries', adverse_events: 'Adverse Events',
  support: 'Support', payments: 'Volunteer payments', qsalud: 'Q Salud',
  statistics: 'Statistics',
};

// URS-F318: dos bloques visuales. SIDEBAR_RESOURCES son las entradas del
// menu lateral; TRANSVERSAL_RESOURCES son funciones que aparecen DENTRO de
// otros modulos (no en el menu) pero cuyo permiso APP filtra el acceso.
const SIDEBAR_RESOURCES = [
  'dashboard', 'agenda', 'volunteers', 'studies', 'clients', 'warehouse',
  'communications', 'recruitment', 'statistics', 'settings', 'audit', 'support',
];
const TRANSVERSAL_RESOURCES = [
  'portal_admin', 'queries', 'adverse_events', 'payments', 'qsalud',
];

const RESOURCE_DESCRIPTIONS_ES = {
  dashboard: 'Visión general de KPIs y métricas del CRO.',
  agenda: 'Calendario de citas y planificación de visitas.',
  volunteers: 'Base de datos de voluntarios y sus historiales.',
  studies: 'Listado y gestión de estudios clínicos.',
  clients: 'Gestión de clientes/promotores.',
  warehouse: 'Almacén de muestras, sondas y materiales.',
  settings: 'Configuración general: usuarios, roles, plantillas, permisos.',
  audit: 'Audit Trail ALCOA+ (registros de cambios).',
  communications: 'Plantillas y campañas de email/SMS.',
  recruitment: 'Gestor de candidatos y campañas de reclutamiento.',
  support: 'Tickets de soporte interno.',
  portal_admin: 'Acceso al Portal del Cliente (sub-app /portal/*).',
  queries: 'Crear / responder queries del Monitor dentro de cualquier estudio.',
  adverse_events: 'Registrar y gestionar Eventos Adversos (EA).',
  payments: 'Pagos a voluntarios — endpoints /api/payments/*.',
  qsalud: 'Pestaña Q Salud del voluntario y botón "Revisar Qsalud" en Agenda.',
  statistics: 'Módulo Estadísticas: KPIs globales de coste real y precio por visita ejecutada entre estudios.',
};
const RESOURCE_DESCRIPTIONS_EN = {
  dashboard: 'CRO KPIs and overview dashboard.',
  agenda: 'Visit scheduling calendar.',
  volunteers: 'Volunteer database and history.',
  studies: 'Clinical studies list & management.',
  clients: 'Client/sponsor management.',
  warehouse: 'Samples, probes & materials warehouse.',
  settings: 'General settings: users, roles, templates, permissions.',
  audit: 'ALCOA+ Audit Trail.',
  communications: 'Email/SMS templates and campaigns.',
  recruitment: 'Candidate manager and recruitment campaigns.',
  support: 'Internal support tickets.',
  portal_admin: 'Access to Client Portal sub-app (/portal/*).',
  queries: 'Create/answer Monitor queries inside any study.',
  adverse_events: 'Register & manage Adverse Events (AE).',
  payments: 'Volunteer payments — /api/payments/* endpoints.',
  qsalud: 'Volunteer "Q Salud" tab and "Review Qsalud" button in Agenda.',
  statistics: 'Statistics module: global KPIs of real cost and price per executed visit across studies.',
};

const SECTION_LABELS_ES = {
  dashboard_estudio: 'Dashboard estudio', datos_generales: 'Datos generales',
  personal: 'Personal', protocolo: 'Protocolo', costes: 'Costes', visitas: 'Visitas',
  voluntarios: 'Voluntarios', cuestionarios: 'Cuestionarios', sondas: 'Sondas',
  muestras: 'Muestras', queries: 'Queries',
  consentimientos: 'Consentimientos', informes: 'Informes',
  delegacion: 'Delegacion', dental: 'Odontograma',
};
const SECTION_LABELS_EN = {
  dashboard_estudio: 'Study Dashboard', datos_generales: 'General data',
  personal: 'Staff', protocolo: 'Protocol', costes: 'Costs', visitas: 'Visits',
  voluntarios: 'Volunteers', cuestionarios: 'Questionnaires', sondas: 'Probes',
  muestras: 'Samples', queries: 'Queries',
  consentimientos: 'Consents', informes: 'Reports',
  delegacion: 'Delegation', dental: 'Dental Chart',
};

const ROLE_LABELS = {
  admin: 'Admin',
  investigador_principal: 'Inv. Principal',
  director_estudio: 'Director Estudio',
  monitor: 'Monitor',
  coordinador: 'Coordinador',
  data_manager: 'Data Manager',
  sondas: 'Sondas',
  supervisor_sondas: 'Supervisor Sondas',
  reclutamiento: 'Reclutamiento',
  supervisor_reclutamiento: 'Supervisor Reclutamiento',
  investigador: 'Co-Investigador',
  odontologia: 'Odontología',
  cliente: 'Cliente (Portal)',
};

function PermissionMatrix({ items, labels, perms, onChange, readonly, descriptions, groups }) {
  // perms: {resource_or_section: {view, edit, delete}}
  // groups (optional): [{label, items, description, color}] → renders with
  // section dividers. If not passed, falls back to legacy flat rendering.
  const renderRow = (key) => {
    const p = perms?.[key] || { view: false, edit: false, delete: false };
    const desc = descriptions?.[key];
    return (
      <tr key={key} className="border-t border-stone-100 hover:bg-stone-50/60">
        <td className="px-3 py-1.5 text-stone-800 font-mono align-top">
          <div className="flex flex-col gap-0.5">
            <span title={desc || ''}>{labels[key] || key}</span>
            {desc && <span className="text-[10px] text-stone-400 font-sans font-normal leading-tight max-w-md">{desc}</span>}
          </div>
        </td>
        <td className="text-center align-middle">
          <Checkbox
            checked={p.view}
            disabled={readonly}
            onCheckedChange={(v) => {
              const view = !!v;
              onChange(key, { view, edit: view ? p.edit : false, delete: view ? p.delete : false });
            }}
            data-testid={`perm-${key}-view`}
          />
        </td>
        <td className="text-center align-middle">
          <Checkbox
            checked={p.edit}
            disabled={readonly || !p.view}
            onCheckedChange={(v) => {
              const edit = !!v;
              onChange(key, { view: p.view, edit, delete: edit ? p.delete : false });
            }}
            data-testid={`perm-${key}-edit`}
          />
        </td>
        <td className="text-center align-middle">
          <Checkbox
            checked={p.delete}
            disabled={readonly || !p.edit}
            onCheckedChange={(v) => {
              onChange(key, { view: p.view, edit: p.edit, delete: !!v });
            }}
            data-testid={`perm-${key}-delete`}
          />
        </td>
      </tr>
    );
  };
  return (
    <div className="overflow-auto border border-stone-200 rounded-sm">
      <table className="w-full text-xs">
        <thead className="bg-stone-50 sticky top-0">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-stone-600">Recurso</th>
            <th className="text-center px-3 py-2 font-medium text-stone-600 w-20">Ver</th>
            <th className="text-center px-3 py-2 font-medium text-stone-600 w-20">Editar</th>
            <th className="text-center px-3 py-2 font-medium text-stone-600 w-20">Eliminar</th>
          </tr>
        </thead>
        <tbody>
          {groups && groups.length > 0 ? (
            groups.map((g, gi) => {
              const visibleItems = g.items.filter(k => items.includes(k));
              if (visibleItems.length === 0) return null;
              return [
                <tr key={`group-${gi}`} className={`${g.color || 'bg-stone-100/70'} border-t-2 border-stone-300`}>
                  <td colSpan={4} className="px-3 py-1.5">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-700">{g.label}</div>
                    {g.description && <div className="text-[10px] text-stone-500 font-normal normal-case tracking-normal mt-0.5">{g.description}</div>}
                  </td>
                </tr>,
                ...visibleItems.map(renderRow),
              ];
            })
          ) : (
            items.map(renderRow)
          )}
        </tbody>
      </table>
    </div>
  );
}

function GroupsTab({ lang }) {
  const labels = lang === 'es' ? RESOURCE_LABELS_ES : RESOURCE_LABELS_EN;
  const [groups, setGroups] = useState([]);
  const [resources, setResources] = useState([]);
  const [selected, setSelected] = useState(null); // group_id
  const [editing, setEditing] = useState(null);   // local copy
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const [gr, ca] = await Promise.all([
      axios.get(`${API_URL}/api/permissions/groups`, { withCredentials: true }),
      axios.get(`${API_URL}/api/permissions/catalog`, { withCredentials: true }),
    ]);
    setGroups(gr.data || []);
    setResources(ca.data?.app_resources || []);
    if (!selected && gr.data?.length) {
      setSelected(gr.data[0].group_id);
      setEditing(JSON.parse(JSON.stringify(gr.data[0])));
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line

  const pickGroup = (g) => {
    setSelected(g.group_id);
    setEditing(JSON.parse(JSON.stringify(g)));
  };

  const updateResource = (key, val) => {
    setEditing(prev => prev ? { ...prev, resources: { ...prev.resources, [key]: val } } : prev);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/permissions/groups/${editing.group_id}`,
        { resources: editing.resources, name: editing.name, description: editing.description },
        { withCredentials: true });
      toast.success(lang === 'es' ? 'Guardado' : 'Saved');
      await reload();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    } finally { setSaving(false); }
  };

  const create = async () => {
    if (!newName.trim()) return;
    try {
      const r = await axios.post(`${API_URL}/api/permissions/groups`,
        { name: newName.trim(), description: newDesc.trim(), resources: {} },
        { withCredentials: true });
      setCreateOpen(false);
      setNewName(''); setNewDesc('');
      await reload();
      setSelected(r.data.group_id);
      setEditing(JSON.parse(JSON.stringify(r.data)));
      toast.success(lang === 'es' ? 'Grupo creado' : 'Group created');
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const removeGroup = async (g) => {
    const n = typeof g.users_count === 'number' ? g.users_count : 0;
    const baseMsg = lang === 'es' ? `Eliminar el grupo "${g.name}"?` : `Delete group "${g.name}"?`;
    const usersWarn = n > 0
      ? (lang === 'es'
          ? `\n\nEste grupo esta asignado actualmente a ${n} ${n === 1 ? 'usuario' : 'usuarios'}. Al eliminarlo, perderan los permisos asociados.`
          : `\n\nThis group is currently assigned to ${n} ${n === 1 ? 'user' : 'users'}. They will lose the associated permissions.`)
      : '';
    const sysWarn = g.is_system
      ? (lang === 'es'
          ? '\n\nATENCION: es un grupo del sistema. Al pulsar "Sincronizar recursos" volvera a crearse con los permisos por defecto.'
          : '\n\nWARNING: this is a system group. Clicking "Sync resources" will recreate it with default permissions.')
      : '';
    if (!confirm(baseMsg + usersWarn + sysWarn)) return;
    try {
      await axios.delete(`${API_URL}/api/permissions/groups/${g.group_id}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Eliminado' : 'Deleted');
      setSelected(null); setEditing(null);
      await reload();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4" data-testid="permissions-groups-tab">
      {/* Groups list */}
      <Card className="border-stone-200 rounded-sm shadow-none">
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-stone-700 uppercase">{lang === 'es' ? 'Grupos' : 'Groups'}</h3>
            <Button size="sm" variant="outline" className="h-7 px-2 gap-1" onClick={() => setCreateOpen(true)} data-testid="permissions-create-group-btn">
              <Plus size={12} /> {lang === 'es' ? 'Nuevo' : 'New'}
            </Button>
          </div>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {groups.map(g => (
              <button key={g.group_id} type="button"
                onClick={() => pickGroup(g)}
                data-testid={`permissions-group-${g.group_id}`}
                className={`w-full text-left px-2 py-1.5 rounded-sm border text-xs flex items-center justify-between gap-2 ${
                  selected === g.group_id ? 'border-[#1f3e80] bg-blue-50/40' : 'border-stone-200 hover:bg-stone-50'
                }`}>
                <div className="min-w-0 flex-1">
                  <p className="text-stone-800 truncate">{g.name}</p>
                  <p className="text-[10px] text-stone-400 truncate font-mono">{g.group_id}</p>
                  {typeof g.users_count === 'number' && g.users_count > 0 && (
                    <p className="text-[10px] text-stone-500 flex items-center gap-0.5 mt-0.5" data-testid={`permissions-group-users-${g.group_id}`}>
                      <Users size={9} weight="fill" /> {g.users_count} {lang === 'es' ? (g.users_count === 1 ? 'usuario' : 'usuarios') : (g.users_count === 1 ? 'user' : 'users')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {g.is_system && (
                    <Lock size={12} className="text-stone-400" weight="fill" title={lang === 'es' ? 'Grupo del sistema' : 'System group'} />
                  )}
                  <button type="button" className="text-stone-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); removeGroup(g); }} data-testid={`permissions-delete-${g.group_id}`}>
                    <Trash size={12} />
                  </button>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Editor */}
      {editing && (
        <Card className="border-stone-200 rounded-sm shadow-none">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-stone-800">{editing.name}</h3>
                  {editing.is_system && (
                    <Badge variant="outline" className="text-[10px] gap-1"><Lock size={9} weight="fill" /> {lang === 'es' ? 'Sistema' : 'System'}</Badge>
                  )}
                  {typeof editing.users_count === 'number' && (
                    <Badge variant="outline" className="text-[10px] gap-1 bg-blue-50 text-blue-700 border-blue-200" data-testid="permissions-editing-users-count">
                      <Users size={9} weight="fill" /> {editing.users_count} {lang === 'es' ? (editing.users_count === 1 ? 'usuario' : 'usuarios') : (editing.users_count === 1 ? 'user' : 'users')}
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-stone-500 mt-0.5">{editing.description || '—'}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeGroup(editing)}
                  className="h-8 gap-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid="permissions-delete-group"
                  title={lang === 'es' ? 'Eliminar este grupo' : 'Delete this group'}
                >
                  <Trash size={14} /> {lang === 'es' ? 'Eliminar' : 'Delete'}
                </Button>
                <Button size="sm" onClick={save} disabled={saving} className="bg-[#1f3e80] hover:bg-[#1a356c] gap-1" data-testid="permissions-save-group">
                  <FloppyDisk size={14} /> {saving ? '…' : (lang === 'es' ? 'Guardar' : 'Save')}
                </Button>
              </div>
            </div>
            <PermissionMatrix
              items={resources}
              labels={labels}
              perms={editing.resources}
              onChange={updateResource}
              readonly={editing.group_id === 'admin'}
              descriptions={lang === 'es' ? RESOURCE_DESCRIPTIONS_ES : RESOURCE_DESCRIPTIONS_EN}
              groups={[
                {
                  label: lang === 'es' ? 'Menú lateral (entradas visibles)' : 'Sidebar menu (visible entries)',
                  description: lang === 'es'
                    ? 'Permisos que controlan qué entradas aparecen en el menú lateral izquierdo.'
                    : 'Permissions that control which entries appear in the left sidebar menu.',
                  color: 'bg-blue-50/50',
                  items: SIDEBAR_RESOURCES,
                },
                {
                  label: lang === 'es' ? 'Permisos transversales (funciones embebidas)' : 'Transversal permissions (embedded features)',
                  description: lang === 'es'
                    ? 'Funciones que NO aparecen en el menú lateral pero cuyo permiso controla el acceso desde otros módulos (estudios, voluntarios, dashboard).'
                    : 'Features that do NOT show in the sidebar but whose permission gates access from other modules (studies, volunteers, dashboard).',
                  color: 'bg-violet-50/60',
                  items: TRANSVERSAL_RESOURCES,
                },
              ]}
            />
            {editing.group_id === 'admin' && (
              <p className="text-[11px] text-amber-700 italic">
                {lang === 'es'
                  ? 'El grupo Admin es super-usuario y no puede modificarse.'
                  : 'Admin group is super-user and cannot be modified.'}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{lang === 'es' ? 'Nuevo grupo' : 'New group'}</DialogTitle>
          </DialogHeader>
          <Input placeholder={lang === 'es' ? 'Nombre' : 'Name'} value={newName} onChange={e => setNewName(e.target.value)} data-testid="permissions-new-group-name" />
          <Input placeholder={lang === 'es' ? 'Descripcion (opcional)' : 'Description (optional)'} value={newDesc} onChange={e => setNewDesc(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={create} disabled={!newName.trim()} className="bg-[#1f3e80] hover:bg-[#1a356c]" data-testid="permissions-new-group-submit">{lang === 'es' ? 'Crear' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StudyRolesTab({ lang }) {
  const sectionLabels = lang === 'es' ? SECTION_LABELS_ES : SECTION_LABELS_EN;
  const [matrix, setMatrix] = useState([]);
  const [sections, setSections] = useState([]);
  const [selected, setSelected] = useState(null); // role_key
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const [m, ca] = await Promise.all([
      axios.get(`${API_URL}/api/permissions/study-roles`, { withCredentials: true }),
      axios.get(`${API_URL}/api/permissions/catalog`, { withCredentials: true }),
    ]);
    setMatrix(m.data || []);
    setSections(ca.data?.study_sections || []);
    if (!selected && m.data?.length) {
      setSelected(m.data[0].role_key);
      setEditing(JSON.parse(JSON.stringify(m.data[0])));
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line

  const pickRole = (r) => { setSelected(r.role_key); setEditing(JSON.parse(JSON.stringify(r))); };

  const updateSection = (key, val) => {
    setEditing(prev => prev ? { ...prev, sections: { ...prev.sections, [key]: val } } : prev);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/permissions/study-roles/${editing.role_key}`,
        { sections: editing.sections }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Guardado' : 'Saved');
      await reload();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4" data-testid="permissions-study-tab">
      <Card className="border-stone-200 rounded-sm shadow-none">
        <CardContent className="p-3 space-y-1">
          <h3 className="text-xs font-semibold text-stone-700 uppercase mb-2">{lang === 'es' ? 'Roles' : 'Roles'}</h3>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {matrix.map(r => (
              <button key={r.role_key} type="button"
                onClick={() => pickRole(r)}
                data-testid={`permissions-study-role-${r.role_key}`}
                className={`w-full text-left px-2 py-1.5 rounded-sm border text-xs ${
                  selected === r.role_key ? 'border-[#1f3e80] bg-blue-50/40' : 'border-stone-200 hover:bg-stone-50'
                }`}>
                <p className="text-stone-800">{ROLE_LABELS[r.role_key] || r.role_key}</p>
                <p className="text-[10px] text-stone-400 font-mono">{r.role_key}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {editing && (
        <Card className="border-stone-200 rounded-sm shadow-none">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-stone-800">{ROLE_LABELS[editing.role_key] || editing.role_key} — {lang === 'es' ? 'permisos por seccion del estudio' : 'permissions by study section'}</h3>
              <Button size="sm" onClick={save} disabled={saving || editing.role_key === 'admin'} className="bg-[#1f3e80] hover:bg-[#1a356c] gap-1" data-testid="permissions-save-study-role">
                <FloppyDisk size={14} /> {saving ? '…' : (lang === 'es' ? 'Guardar' : 'Save')}
              </Button>
            </div>
            <PermissionMatrix
              items={sections}
              labels={sectionLabels}
              perms={editing.sections}
              onChange={updateSection}
              readonly={editing.role_key === 'admin'}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AuditTab({ lang }) {
  const resourceLabels = lang === 'es' ? RESOURCE_LABELS_ES : RESOURCE_LABELS_EN;
  const sectionLabels = lang === 'es' ? SECTION_LABELS_ES : SECTION_LABELS_EN;
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [studies, setStudies] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedStudyId, setSelectedStudyId] = useState('');
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, s] = await Promise.all([
          axios.get(`${API_URL}/api/users`, { withCredentials: true }),
          axios.get(`${API_URL}/api/studies`, { withCredentials: true }),
        ]);
        setUsers(u.data || []);
        setStudies(s.data || []);
      } catch (_e) { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    if (!selectedUser) { setAudit(null); return; }
    setLoading(true);
    const url = `${API_URL}/api/permissions/audit/${selectedUser.id}` +
      (selectedStudyId ? `?study_id=${encodeURIComponent(selectedStudyId)}` : '');
    axios.get(url, { withCredentials: true })
      .then(r => setAudit(r.data))
      .catch(() => setAudit(null))
      .finally(() => setLoading(false));
  }, [selectedUser, selectedStudyId]);

  const filteredUsers = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.email || '').toLowerCase().includes(q)
        || (u.name || '').toLowerCase().includes(q);
  });

  const renderCell = (cell) => {
    if (!cell) return <span className="text-stone-300">—</span>;
    if (cell.granted) {
      return (
        <div className="flex flex-col items-center gap-0.5">
          <Check size={14} weight="bold" className="text-green-600" />
          <div className="flex flex-wrap justify-center gap-0.5 max-w-[120px]">
            {(cell.sources || []).map((src, i) => (
              <Badge key={i} variant="outline" className={`text-[9px] px-1 py-0 ${src === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' : src.startsWith('override:') ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                {src}
              </Badge>
            ))}
          </div>
        </div>
      );
    }
    return <X size={14} className="text-stone-300 mx-auto" />;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4" data-testid="permissions-audit-tab">
      {/* Users list */}
      <Card className="border-stone-200 rounded-sm shadow-none">
        <CardContent className="p-3 space-y-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={lang === 'es' ? 'Buscar usuario...' : 'Search user...'}
              className="pl-7 h-8 text-xs"
              data-testid="permissions-audit-search"
            />
          </div>
          <div className="space-y-0.5 max-h-[60vh] overflow-y-auto">
            {filteredUsers.map(u => {
              const sel = selectedUser?.id === u.id;
              const userRoles = u.roles || (u.role ? [u.role] : []);
              const isAdminUser = userRoles.includes('admin');
              return (
                <button key={u.id} type="button"
                  onClick={() => setSelectedUser(u)}
                  data-testid={`permissions-audit-user-${u.id}`}
                  className={`w-full text-left px-2 py-1.5 rounded-sm border text-xs ${sel ? 'border-[#1f3e80] bg-blue-50/40' : 'border-stone-200 hover:bg-stone-50'}`}>
                  <div className="flex items-center justify-between gap-1">
                    <p className="text-stone-800 truncate flex-1">{u.name}</p>
                    {isAdminUser && <Shield size={10} weight="fill" className="text-purple-500 flex-shrink-0" />}
                  </div>
                  <p className="text-[10px] text-stone-400 font-mono truncate">{u.email}</p>
                </button>
              );
            })}
            {filteredUsers.length === 0 && (
              <p className="text-xs text-stone-400 text-center py-4">{lang === 'es' ? 'Sin resultados' : 'No results'}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Audit detail */}
      <Card className="border-stone-200 rounded-sm shadow-none">
        <CardContent className="p-4 space-y-4">
          {!selectedUser ? (
            <p className="text-sm text-stone-400 text-center py-12">
              {lang === 'es' ? 'Selecciona un usuario para auditar sus permisos efectivos' : 'Select a user to audit their effective permissions'}
            </p>
          ) : loading ? (
            <p className="text-sm text-stone-400 text-center py-12">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
          ) : audit ? (
            <>
              <div className="border-b border-stone-200 pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-stone-800">{audit.user.name}</h3>
                      {audit.user.is_admin && (
                        <Badge className="text-[10px] bg-purple-100 text-purple-800 border-purple-200 gap-1" data-testid="audit-admin-badge">
                          <Shield size={10} weight="fill" /> Super-usuario
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-stone-500 font-mono">{audit.user.email}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {audit.user.roles.map(r => (
                        <Badge key={r} variant="outline" className="text-[10px]">{ROLE_LABELS[r] || r}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-[11px] flex flex-wrap gap-2 items-center">
                  <span className="text-stone-500">{lang === 'es' ? 'Origen de grupos:' : 'Group source:'}</span>
                  <Badge variant="outline" className={`text-[10px] ${audit.groups_source === 'explicit' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-stone-50 text-stone-600'}`} data-testid="audit-groups-source">
                    {audit.groups_source === 'explicit' ? (lang === 'es' ? 'Asignado explicitamente' : 'Explicitly assigned') : (lang === 'es' ? 'Por rol (fallback)' : 'Role fallback')}
                  </Badge>
                  <span className="text-stone-500 ml-2">{lang === 'es' ? 'Grupos:' : 'Groups:'}</span>
                  {audit.groups.map(g => (
                    <Badge key={g.group_id} variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                      {g.is_system && <Lock size={9} weight="fill" className="mr-0.5" />}
                      {g.name}
                    </Badge>
                  ))}
                  {audit.groups.length === 0 && (
                    <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
                      {lang === 'es' ? 'Sin grupos' : 'No groups'}
                    </Badge>
                  )}
                </div>
              </div>

              {/* App-level matrix */}
              <div>
                <h4 className="text-xs font-semibold text-stone-700 uppercase mb-2">{lang === 'es' ? 'Permisos a nivel aplicacion' : 'App-level permissions'}</h4>
                <div className="overflow-auto border border-stone-200 rounded-sm">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-50">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-stone-600">{lang === 'es' ? 'Recurso' : 'Resource'}</th>
                        <th className="text-center px-3 py-2 font-medium text-stone-600 w-32">{lang === 'es' ? 'Ver' : 'View'}</th>
                        <th className="text-center px-3 py-2 font-medium text-stone-600 w-32">{lang === 'es' ? 'Editar' : 'Edit'}</th>
                        <th className="text-center px-3 py-2 font-medium text-stone-600 w-32">{lang === 'es' ? 'Eliminar' : 'Delete'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(audit.app || {}).map(([resource, acts]) => (
                        <tr key={resource} className="border-t border-stone-100 hover:bg-stone-50/60" data-testid={`audit-app-${resource}`}>
                          <td className="px-3 py-1.5 text-stone-800">{resourceLabels[resource] || resource}</td>
                          <td className="text-center py-1.5">{renderCell(acts.view)}</td>
                          <td className="text-center py-1.5">{renderCell(acts.edit)}</td>
                          <td className="text-center py-1.5">{renderCell(acts.delete)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Study selector + matrix */}
              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <h4 className="text-xs font-semibold text-stone-700 uppercase">{lang === 'es' ? 'Permisos por seccion en estudio' : 'Per-study section permissions'}</h4>
                  <select
                    value={selectedStudyId}
                    onChange={(e) => setSelectedStudyId(e.target.value)}
                    className="h-7 text-xs border border-stone-200 rounded-sm px-2 bg-white"
                    data-testid="permissions-audit-study-select"
                  >
                    <option value="">{lang === 'es' ? '-- Selecciona estudio --' : '-- Select study --'}</option>
                    {studies.map(s => (
                      <option key={s.study_id} value={s.study_id}>{s.code} — {s.title}</option>
                    ))}
                  </select>
                </div>
                {!audit.study ? (
                  <p className="text-xs text-stone-400 italic">{lang === 'es' ? 'Selecciona un estudio para ver los permisos en sus secciones.' : 'Select a study to see section permissions.'}</p>
                ) : (
                  <>
                    <div className="text-[11px] text-stone-500 mb-1 flex items-center gap-1 flex-wrap">
                      {audit.study.staff_roles?.length ? (
                        <>
                          <span>{lang === 'es' ? 'Roles en el staff:' : 'Staff roles:'}</span>
                          {audit.study.staff_roles.map(r => <Badge key={r} variant="outline" className="text-[10px]">{ROLE_LABELS[r] || r}</Badge>)}
                        </>
                      ) : (
                        <span className="italic">{lang === 'es' ? 'Usuario no asignado al staff. Se aplican los roles globales.' : 'User not on staff; global roles apply.'}</span>
                      )}
                    </div>
                    <div className="overflow-auto border border-stone-200 rounded-sm">
                      <table className="w-full text-xs">
                        <thead className="bg-stone-50">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-stone-600">{lang === 'es' ? 'Seccion' : 'Section'}</th>
                            <th className="text-center px-3 py-2 font-medium text-stone-600 w-32">{lang === 'es' ? 'Ver' : 'View'}</th>
                            <th className="text-center px-3 py-2 font-medium text-stone-600 w-32">{lang === 'es' ? 'Editar' : 'Edit'}</th>
                            <th className="text-center px-3 py-2 font-medium text-stone-600 w-32">{lang === 'es' ? 'Eliminar' : 'Delete'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(audit.study.sections || {}).map(([sec, acts]) => (
                            <tr key={sec} className="border-t border-stone-100 hover:bg-stone-50/60" data-testid={`audit-study-${sec}`}>
                              <td className="px-3 py-1.5 text-stone-800">{sectionLabels[sec] || sec}</td>
                              <td className="text-center py-1.5">{renderCell(acts.view)}</td>
                              <td className="text-center py-1.5">{renderCell(acts.edit)}</td>
                              <td className="text-center py-1.5">{renderCell(acts.delete)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-red-500 text-center py-12">{lang === 'es' ? 'Error cargando datos' : 'Error loading data'}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function PermissionsTab({ lang }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncKey, setSyncKey] = useState(0); // bump to force-reload child tabs after sync

  useEffect(() => {
    axios.get(`${API_URL}/api/permissions/me`, { withCredentials: true })
      .then(r => setIsAdmin(!!r.data?.is_admin))
      .catch(() => setIsAdmin(false));
  }, []);

  const handleSync = async () => {
    const msg = lang === 'es'
      ? 'Esta accion anadira los recursos/secciones nuevos a todos los grupos y roles existentes (sin sobrescribir los permisos ya configurados). Continuar?'
      : 'This action will add new resources/sections to every existing group and role (without overwriting already configured permissions). Continue?';
    if (!confirm(msg)) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/permissions/sync-resources`, {}, { withCredentials: true });
      const gAdded = (data.groups_updated || []).reduce((n, g) => n + (g.added?.length || 0), 0);
      const rAdded = (data.roles_updated || []).reduce((n, r) => n + (r.added?.length || 0), 0);
      const gDedup = (data.groups_dedup || []).reduce((n, g) => n + (g.removed_ids?.length || 0), 0);
      const rDedup = (data.roles_dedup || []).reduce((n, r) => n + (r.removed_ids?.length || 0), 0);
      const summary = lang === 'es'
        ? `Sincronizacion completada. Duplicados eliminados: ${gDedup} grupos, ${rDedup} roles. Grupos actualizados: ${data.groups_updated.length} (${gAdded} claves), roles de estudio: ${data.roles_updated.length} (${rAdded} claves). Nuevos: ${data.groups_created.length} grupos, ${data.roles_created.length} roles.`
        : `Sync done. Removed duplicates: ${gDedup} groups, ${rDedup} roles. Groups updated: ${data.groups_updated.length} (${gAdded} keys), study roles: ${data.roles_updated.length} (${rAdded} keys). Created: ${data.groups_created.length} groups, ${data.roles_created.length} roles.`;
      toast.success(summary);
      setSyncKey(k => k + 1);
    } catch (e) {
      toast.error(errText(e, 'Error'));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div data-testid="permissions-tab">
      {isAdmin && (
        <div className="flex items-center justify-end mb-3">
          <Button
            size="sm"
            variant="outline"
            onClick={handleSync}
            disabled={syncing}
            className="h-8 gap-1.5 border-[#1f3e80]/30 text-[#1f3e80] hover:bg-[#1f3e80]/5"
            data-testid="permissions-sync-resources-btn"
            title={lang === 'es'
              ? 'Anade los recursos/secciones nuevos a todos los grupos y roles existentes sin sobrescribir lo ya configurado'
              : 'Adds new resources/sections to every existing group and role without overwriting current configuration'}
          >
            <ArrowsClockwise size={14} className={syncing ? 'animate-spin' : ''} />
            {syncing
              ? (lang === 'es' ? 'Sincronizando...' : 'Syncing...')
              : (lang === 'es' ? 'Sincronizar recursos' : 'Sync resources')}
          </Button>
        </div>
      )}
      <Tabs defaultValue="groups">
        <TabsList className="bg-stone-50 border border-stone-200">
          <TabsTrigger value="groups" data-testid="subtab-perm-groups">
            {lang === 'es' ? 'Permisos transversales (App)' : 'Transversal permissions (App)'}
          </TabsTrigger>
          <TabsTrigger value="study-roles" data-testid="subtab-perm-study">
            {lang === 'es' ? 'Permisos por estudio (rol)' : 'Study permissions (role)'}
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="subtab-perm-audit">
            {lang === 'es' ? 'Auditoria' : 'Audit'}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="groups" className="mt-4"><GroupsTab key={`g-${syncKey}`} lang={lang} /></TabsContent>
        <TabsContent value="study-roles" className="mt-4"><StudyRolesTab key={`s-${syncKey}`} lang={lang} /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditTab key={`a-${syncKey}`} lang={lang} /></TabsContent>
      </Tabs>
    </div>
  );
}

export default PermissionsTab;
